import { isValidElement } from 'react';
import {
  Button,
  ButtonSize,
  ButtonStyles,
  Theme,
  useTranslation,
  NextgenImage,
  useBreakpoint,
  Breakpoints,
  useSession,
} from '@nextgen-web-framework/all';
import { ConfigService } from '@nextgen-web-framework/core';
import { Errors, type ErrorCardProps, type ErrorsType } from './types/propsTypes';
import styles from './ErrorCard.module.scss';

const getErrorDetails = (type: ErrorsType, props: ErrorCardProps) => {
  let buttonLabel = props.buttonLabel ?? `web.error.button.backToAmway`;
  let { imageUrl, title, description } = props;
  const configs = ConfigService.getConfigs();
  const assetsBasePath = configs.NEXT_ASSETS_BASE_PATH as string;
  switch (type) {
    case Errors.Maintenance:
      imageUrl ??= `${assetsBasePath}/images/Maintenance.svg`;
      title ??= 'web.error.maintenance.title';
      description ??= 'web.error.maintenance.description';
      break;
    case Errors.NotFound:
      imageUrl ??= `${assetsBasePath}/images/NoResult.svg`;
      title ??= 'web.error.notfound.title';
      description ??= 'web.error.notfound.description';
      break;
    case Errors.Restricted:
      imageUrl ??= `${assetsBasePath}/images/AuthenticationFailed.svg`;
      title ??= 'web.error.restricted.title';
      description ??= 'web.error.restricted.description';
      break;
    case Errors.General:
    default:
      buttonLabel ??= 'web.error.button.reloadPage';
      imageUrl ??= `${assetsBasePath}/images/General.svg`;
      title ??= 'web.error.general.title';
      description ??= 'web.error.general.description';
  }

  return {
    buttonLabel,
    imageUrl,
    title,
    description,
  };
};

const ErrorCard = (props: ErrorCardProps) => {
  const { isAnonymous } = useSession();
  const viewport = useBreakpoint() as Breakpoints;
  const buttonSize = [Breakpoints.MD].includes(viewport) ? ButtonSize.SMALL : ButtonSize.MEDIUM;
  const { type, customCtaComponent } = props;
  const t = useTranslation();
  const { imageUrl, buttonLabel, title, description } = getErrorDetails(type, props);
  const simpleBtn = (
    <Button
      buttonStyle={ButtonStyles.OUTLINE}
      onClick={props.onActionClick}
      size={buttonSize}
      theme={Theme.BLACK}
      title={t(buttonLabel)}
      type="button"
    />
  );
  const getButtonToRender = () => {
    let errorCardButton: React.ReactNode;
    if (isValidElement(customCtaComponent)) {
      errorCardButton = customCtaComponent;
    } else if (type === Errors.Restricted && isAnonymous()) {
      errorCardButton = isValidElement(props.restrictedButton) ? props.restrictedButton : simpleBtn;
    } else {
      errorCardButton = simpleBtn;
    }
    return errorCardButton;
  };

  const buttonToRender = getButtonToRender();
  return (
    <div className={`${styles['error-card-container']} ${props.className}`} data-testid={`error-card-${type}-testid`}>
      <div className={styles['error-card-inner-wrapper']}>
        <div className={props.customImageClass ?? styles['error-card-image-container']}>
          <NextgenImage src={imageUrl} alt={`${type}`} className={styles['error-card-image']} />
        </div>
        <div className={styles['error-card-info']}>
          <div className={styles['error-card-title']}>{t(title)}</div>
          <div className={styles['error-card-description']}>{t(description)}</div>
        </div>
        <div className={styles['error-card-button']}>{buttonToRender}</div>
      </div>
    </div>
  );
};

export default ErrorCard;
export { ErrorCardProps, Errors };
